// Transforma una serie demográfica (geografía, edad, profesión, etc.) en datos
// listos para un Chart.js bar chart, aplicando Top N + agrupación "Otros" para
// mantener el eje X legible cuando hay muchas categorías.

export interface BarChartData {
  labels: string[];
  values: number[];
  total: number;          // sumatoria original (incluye lo que cayó en "Otros")
  truncated: number;      // cuántos items originales se agruparon en "Otros" (0 si no hubo cap)
}

const OTHERS_LABEL = 'Otros';

export function buildBarChartData(
  items: any[] | null | undefined,
  labelKey: string,
  valueKey: string,
  topN: number,
  formatLabel?: (raw: any) => string
): BarChartData {
  if (!items || items.length === 0) {
    return { labels: [], values: [], total: 0, truncated: 0 };
  }

  // Normaliza values a number (filtra null/undefined/NaN) y conserva label crudo
  const normalized = items
    .map(item => ({
      rawLabel: item?.[labelKey],
      value: Number(item?.[valueKey] ?? 0)
    }))
    .filter(entry => Number.isFinite(entry.value) && entry.value > 0);

  if (normalized.length === 0) {
    return { labels: [], values: [], total: 0, truncated: 0 };
  }

  // Orden desc por value para que Top N sea el "top real"
  normalized.sort((a, b) => b.value - a.value);

  const totalOriginal = normalized.reduce((sum, e) => sum + e.value, 0);
  const applyFormat = (raw: any): string =>
    formatLabel ? formatLabel(raw) : String(raw ?? 'Sin especificar');

  // Sin cap
  if (normalized.length <= topN) {
    return {
      labels: normalized.map(e => applyFormat(e.rawLabel)),
      values: normalized.map(e => e.value),
      total: totalOriginal,
      truncated: 0
    };
  }

  // Con cap: top (N-1) + "Otros"
  const visibleCount = Math.max(topN - 1, 1);
  const topItems = normalized.slice(0, visibleCount);
  const restItems = normalized.slice(visibleCount);
  const othersSum = restItems.reduce((sum, e) => sum + e.value, 0);

  return {
    labels: [...topItems.map(e => applyFormat(e.rawLabel)), OTHERS_LABEL],
    values: [...topItems.map(e => e.value), othersSum],
    total: totalOriginal,
    truncated: restItems.length
  };
}
